import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { X, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FEATURE_FOLD } from '@/lib/productUpdateForm';

/**
 * Presentational half of the member popup.
 *
 * `ProductUpdateCard` is the pure layout (no Dialog, no data fetching, no
 * mutations) so it can be rendered by the member wrapper, by the admin
 * preview, and by static-markup unit tests.
 * `ProductUpdatePopupView` wraps the card in the Dialog shell.
 */

export interface PopupFeature {
  /** Persisted features have a numeric id; unsaved preview rows get a synthetic one. */
  id?: number | string;
  title: string;
  description: string;
  learnMoreUrl: string | null;
}

export interface PopupUpdate {
  id?: number;
  version: string;
  releaseDate: string;
  headline: string;
  heroImageUrl: string | null;
  heroDescription: string;
  features: PopupFeature[];
}

interface CardProps {
  update: PopupUpdate;
  onDismiss: () => void;
  /** In preview the history link must not navigate away from the editor. */
  previewMode?: boolean;
}

function formatReleaseDate(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function ProductUpdateCard({ update, onDismiss, previewMode = false }: CardProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const [foldHeight, setFoldHeight] = useState<number | null>(null);

  const features = update.features;
  const releaseDate = formatReleaseDate(update.releaseDate);
  const meta = [update.version ? `v${update.version}` : null, releaseDate].filter(Boolean).join(' · ');

  const updateScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  // Cap the list at exactly FEATURE_FOLD rows so the fold matches what the
  // admin editor marks. Rows are variable height, so measure the sixth row.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const measure = () => {
      const sixth = el.children[FEATURE_FOLD] as HTMLElement | undefined;
      if (features.length > FEATURE_FOLD && sixth) {
        const fifth = el.children[FEATURE_FOLD - 1] as HTMLElement;
        const gap = sixth.offsetTop - (fifth.offsetTop + fifth.offsetHeight);
        setFoldHeight(sixth.offsetTop - gap / 2);
      } else {
        setFoldHeight(null);
      }
      updateScrollState();
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [features, updateScrollState]);

  const historyProps = previewMode
    ? {
        href: '#',
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          onDismiss();
        },
        title: 'Navigation is disabled in preview',
      }
    : { href: '/product-updates', onClick: onDismiss };

  return (
    <div className="relative flex max-h-[calc(100dvh-2rem)] flex-col">
      {/* Close button */}
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-4 right-4 z-10 rounded-full p-1.5 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/50 dark:hover:bg-orange-900 text-orange-700 hover:text-orange-900 dark:text-orange-300 dark:hover:text-orange-100 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Hero banner: full width, 16:9, collapses entirely when absent */}
      {update.heroImageUrl && (
        <div className="shrink-0 w-full aspect-video max-h-[30vh] overflow-hidden bg-gray-100 dark:bg-gray-800">
          <img
            src={update.heroImageUrl}
            alt={update.headline}
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col p-6 pb-5">
        <div className="shrink-0">
          <div className="mb-4">
            <span className="inline-block rounded-full bg-orange-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
              NEW
            </span>
          </div>
          {meta && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{meta}</p>
          )}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight break-words">
            {update.headline}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-gray-600 dark:text-gray-400 break-words">
            {update.heroDescription}
          </p>
        </div>

        {/* Feature highlights: every feature reachable, first five above the fold */}
        {features.length > 0 && (
          <div className="relative mt-5 min-h-0 flex-1">
            <div
              ref={listRef}
              onScroll={updateScrollState}
              style={foldHeight ? { maxHeight: foldHeight } : undefined}
              className="relative h-full space-y-3 overflow-y-auto pr-2 [scrollbar-width:thin]"
              data-testid="feature-list"
            >
              {features.map((feature, idx) => (
                <div key={feature.id ?? `feature-${idx}`} className="flex items-start gap-3 min-w-0">
                  {/* h-5 matches the text-sm line height so the mark centres on the first line */}
                  <div className="flex h-5 flex-shrink-0 items-center">
                    <CheckCircle2 className="block h-4 w-4 text-orange-500" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 break-words text-sm leading-5">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {feature.title}.
                    </span>{' '}
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </span>
                    {feature.learnMoreUrl && (
                      <>
                        {' '}
                        <a
                          href={feature.learnMoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:underline"
                        >
                          [Learn more]
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Scroll affordance: fade that disappears at the bottom */}
            <div
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white/95 to-transparent dark:from-gray-950 transition-opacity',
                hasMoreBelow ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex shrink-0 flex-col gap-3">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full rounded-lg bg-orange-600 hover:bg-orange-700 px-4 py-2.5 text-sm font-medium text-white transition-colors"
          >
            Got it!
          </button>
          <a
            {...historyProps}
            className="text-center text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:underline"
          >
            View Product History
          </a>
        </div>
      </div>
    </div>
  );
}

interface ViewProps extends CardProps {
  open: boolean;
}

export function ProductUpdatePopupView({ open, update, onDismiss, previewMode }: ViewProps) {
  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) onDismiss(); }}>
      <DialogContent
        className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl border-0 shadow-2xl [&>button]:hidden"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">
          {previewMode ? 'Preview: ' : ''}Product Update{update.version ? ` v${update.version}` : ''}
        </DialogTitle>
        <ProductUpdateCard update={update} onDismiss={onDismiss} previewMode={previewMode} />
      </DialogContent>
    </Dialog>
  );
}
